import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth, readSavedLoginEmail, saveLoginEmail, clearSavedLoginEmail } from '../context/AuthContext';
import { LAST_LOGOUT_KEY } from '../services/_apiBase';

type EmailStep = 'enterEmail' | 'enterCode';

// Koden gäller i 10 minuter (samma som CODE_TTL_MINUTES i admin-api).
const CODE_TTL_MS = 10 * 60 * 1000;
// Kom ihåg att en kod är skickad. Behövs i PWA:n: när man byter till mejlappen
// för att läsa koden stängs appen ofta i bakgrunden, och sidan laddas om när
// man kommer tillbaka. Utan detta hamnar man på e-poststeget igen och måste
// skicka en ny kod.
const PENDING_CODE_KEY = 'iot_login_pending_code';

type PendingCode = { email: string; sentAt: number };

const readPendingCode = (): PendingCode | null => {
    try {
        const raw = localStorage.getItem(PENDING_CODE_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw) as PendingCode;
        if (!p?.email || typeof p.sentAt !== 'number') return null;
        if (Date.now() - p.sentAt > CODE_TTL_MS) { localStorage.removeItem(PENDING_CODE_KEY); return null; }
        return p;
    } catch { return null; }
};
const savePendingCode = (email: string): void => {
    try { localStorage.setItem(PENDING_CODE_KEY, JSON.stringify({ email, sentAt: Date.now() })); } catch { /* privat läge m.m. */ }
};
const clearPendingCode = (): void => {
    try { localStorage.removeItem(PENDING_CODE_KEY); } catch { /* ignorera */ }
};

type LastLogout = { at: string; request: string; message: string };

const readLastLogout = (): LastLogout | null => {
    try {
        const raw = localStorage.getItem(LAST_LOGOUT_KEY);
        return raw ? JSON.parse(raw) as LastLogout : null;
    } catch { return null; }
};

const Login: React.FC = () => {
    const { requestOtp, verifyOtp, token } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Hela adressen inklusive parametrar, så att en pushlänk till en viss flik
    // fungerar även när man först måste logga in.
    const fromLoc = (location.state as any)?.from;
    const from = fromLoc?.pathname ? `${fromLoc.pathname}${fromLoc.search ?? ''}` : '/';

    // Läses en gång vid uppstart – har vi nyss skickat en kod fortsätter vi på kodsteget.
    const [pending] = useState(readPendingCode);
    const [emailStep, setEmailStep] = useState<EmailStep>(pending ? 'enterCode' : 'enterEmail');
    const [email, setEmail] = useState(() => pending?.email ?? readSavedLoginEmail());
    // Sant så länge fältet visar den sparade adressen orörd – då visas "Inte du?".
    const [emailIsSaved, setEmailIsSaved] = useState(() => readSavedLoginEmail() !== '');
    const [code, setCode] = useState('');
    const [lastLogout] = useState(readLastLogout);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(
        pending ? 'Om e-postadressen finns hos oss är en sexsiffrig kod på väg. Den gäller i 10 minuter.' : null
    );

    // Redan inloggad: gå vidare direkt. PWA:n kan starta på /login (t.ex. en
    // hemskärmsgenväg skapad från inloggningssidan) och skulle annars be om en
    // ny kod fast inloggningen finns kvar.
    if (token) return <Navigate to={from} replace />;

    const resetMessages = () => { setError(null); setInfo(null); };

    const handleRequestCode = async (e: React.SyntheticEvent) => {
        e.preventDefault();
        resetMessages();
        setLoading(true);
        try {
            await requestOtp(email.trim());
            savePendingCode(email.trim());
            setInfo('Om e-postadressen finns hos oss är en sexsiffrig kod på väg. Den gäller i 10 minuter.');
            setEmailStep('enterCode');
            setCode('');
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || 'Kunde inte skicka koden');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        resetMessages();
        setLoading(true);
        try {
            await verifyOtp(email.trim(), code.trim());
            clearPendingCode();
            saveLoginEmail(email.trim());
            try { localStorage.removeItem(LAST_LOGOUT_KEY); } catch { /* ignorera */ }
            navigate(from, { replace: true });
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || 'Fel kod, eller så har den gått ut');
        } finally {
            setLoading(false);
        }
    };

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '10px 12px', fontSize: '16px',
        border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box',
        outline: 'none', transition: 'border-color 0.2s'
    };
    const buttonStyle = (disabled: boolean): React.CSSProperties => ({
        width: '100%', padding: '11px', fontSize: '15px', fontWeight: 'bold',
        backgroundColor: disabled ? '#6c9bd1' : '#007bff', color: 'white',
        border: 'none', borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s'
    });
    const linkButtonStyle: React.CSSProperties = {
        background: 'none', border: 'none', color: '#007bff', cursor: 'pointer',
        fontSize: '13px', padding: 0, textDecoration: 'underline'
    };
    const labelStyle: React.CSSProperties = {
        display: 'block', fontWeight: 'bold', fontSize: '13px', marginBottom: '6px', color: '#444'
    };

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#f0f2f5', fontFamily: 'Arial, sans-serif',
            padding: '16px',
        }}>
            <div style={{
                backgroundColor: 'white', borderRadius: '10px', padding: '32px 28px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.10)', width: '100%', maxWidth: '380px',
                boxSizing: 'border-box',
            }}>
                {/* Logo / title */}
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
                        {/* DEVIOT diamond mark */}
<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="222" height="73">
<path d="M0 0 C73.26 0 146.52 0 222 0 C222 24.09 222 48.18 222 73 C148.74 73 75.48 73 0 73 C0 48.91 0 24.82 0 0 Z " fill="#FBFBFB" transform="translate(0,0)"/>
<path d="M0 0 C-1.41233567 4.4937953 -3.58098311 6.8131703 -7 10 C-7.98877379 10.98077377 -8.97338792 11.96575447 -9.9543457 12.9543457 C-17.63623047 20.63623047 -25.31811523 28.31811523 -33 36 C-29.84918206 39.79966033 -26.66007076 43.40552772 -23.09765625 46.82421875 C-22.43572266 47.46037109 -21.77378906 48.09652344 -21.09179688 48.75195312 C-19.7187408 50.06157638 -18.33828535 51.36347834 -16.95117188 52.65820312 C-16.29826172 53.29048828 -15.64535156 53.92277344 -14.97265625 54.57421875 C-14.37718994 55.13713623 -13.78172363 55.70005371 -13.16821289 56.2800293 C-12 58 -12 58 -12.25170898 60.08911133 C-13 62 -13 62 -14 64 C-14.66 64 -15.32 64 -16 64 C-16.33 64.99 -16.66 65.98 -17 67 C-21.48116656 66.58121829 -24.02769985 63.43993567 -27.04296875 60.42578125 C-27.89656731 59.58360298 -27.89656731 59.58360298 -28.76741028 58.72441101 C-29.96720822 57.53540693 -31.1630644 56.34241391 -32.35522461 55.14575195 C-34.17765135 53.3223539 -36.02031438 51.52130368 -37.86523438 49.72070312 C-39.02994554 48.56119992 -40.19339999 47.40043269 -41.35546875 46.23828125 C-41.90647751 45.70366776 -42.45748627 45.16905426 -43.02519226 44.61824036 C-45.41752871 42.1820431 -46.95194763 40.12690134 -48.1652832 36.92260742 C-48 34 -48 34 -45.95874023 31.55200195 C-44.98235596 30.70984131 -44.00597168 29.86768066 -43 29 C-41.98366922 27.93644674 -40.98177102 26.85896996 -39.99243164 25.77026367 C-37.02990254 22.55752765 -34.02434507 19.38886132 -31.00170898 16.23266602 C-29.71200336 14.87249177 -28.43513249 13.50002834 -27.17163086 12.11547852 C-18.67740415 2.83227329 -13.27406374 -2.61387106 0 0 Z " fill="#2A7DF7" transform="translate(49,0)"/>
<path d="M0 0 C2.9848808 2.30262233 5.72288767 4.69465829 8.36987305 7.37426758 C10.43492568 9.4337532 12.54117033 11.44841374 14.64453125 13.46875 C16.36750171 15.14551052 18.09014947 16.82260269 19.8125 18.5 C20.69615234 19.34175781 21.57980469 20.18351562 22.49023438 21.05078125 C23.32490234 21.86933594 24.15957031 22.68789063 25.01953125 23.53125 C25.78370361 24.26923828 26.54787598 25.00722656 27.33520508 25.76757812 C29.3775666 28.50630136 29.36878093 29.65003769 29 33 C27.80343628 34.89204407 27.80343628 34.89204407 26.10107422 36.6887207 C25.46555634 37.36904861 24.83003845 38.04937653 24.17526245 38.75032043 C23.47547272 39.4682225 22.77568298 40.18612457 22.0546875 40.92578125 C21.3332254 41.67337204 20.61176331 42.42096283 19.86843872 43.19120789 C18.33359251 44.76890702 16.7914506 46.33953631 15.24267578 47.90356445 C12.89760529 50.28950078 10.60808974 52.72109822 8.31640625 55.15820312 C-3.26904838 67.10895643 -3.26904838 67.10895643 -9.24121094 68.04052734 C-12.67457806 68.04268996 -15.66901766 67.80284359 -19 67 C-17.64768813 63.97022571 -16.14519457 61.91527387 -13.76953125 59.609375 C-13.1348291 58.98627441 -12.50012695 58.36317383 -11.84619141 57.72119141 C-11.17507324 57.07069824 -10.50395508 56.42020508 -9.8125 55.75 C-5.23585903 51.25098404 -0.76983529 46.76013113 3.37109375 41.8515625 C6.71680555 38.04852319 10.38316877 34.54301835 14 31 C11.06523332 27.39442951 8.0684997 24.0266682 4.671875 20.8515625 C4.08043701 20.29041748 3.48899902 19.72927246 2.87963867 19.15112305 C1.67773042 18.01768303 0.46177519 16.89893847 -0.76831055 15.79614258 C-2.38302997 14.26135746 -3.773882 12.8607505 -5 11 C-5.0119557 6.06827536 -3.27324487 3.5900105 0 0 Z " fill="#14BFB0" transform="translate(56,6)"/>
<path d="M0 0 C14.06896552 0 14.06896552 0 18 3 C20.96912141 8.85289743 20.3294465 15.02833202 20.3125 21.4375 C20.32861328 22.65244141 20.34472656 23.86738281 20.36132812 25.11914062 C20.36197266 26.29541016 20.36261719 27.47167969 20.36328125 28.68359375 C20.36626221 29.75150146 20.36924316 30.81940918 20.37231445 31.91967773 C19.92926969 35.58518314 19.02411361 37.91233632 17 41 C12 43 12 43 0 43 C0 28.81 0 14.62 0 0 Z " fill="#010101" transform="translate(94,16)"/>
<path d="M0 0 C4.56944444 2.13888889 4.56944444 2.13888889 6 5 C6.12283485 7.43399109 6.18831884 9.8383157 6.203125 12.2734375 C6.20882507 12.99359039 6.21452515 13.71374329 6.22039795 14.45571899 C6.22984835 15.98055553 6.23638209 17.50541249 6.24023438 19.03027344 C6.24993373 21.35908812 6.2809524 23.68701776 6.3125 26.015625 C6.31903087 27.49739059 6.32428101 28.9791625 6.328125 30.4609375 C6.3404718 31.15585297 6.3528186 31.85076843 6.36553955 32.56674194 C6.35152382 35.62071219 6.15816878 37.71419538 4.66699219 40.40869141 C1.62682342 43.31083259 -1.92214101 43.39298213 -5.99609375 43.35546875 C-9.4250568 42.74721215 -10.95649153 40.66275346 -13 38 C-14.6565656 33.03030319 -14.26424254 27.86344523 -14.29516602 22.65820312 C-14.30669239 21.3062087 -14.32715523 19.95426001 -14.35668945 18.60253906 C-14.39945917 16.6442118 -14.41294668 14.6853147 -14.42578125 12.7265625 C-14.44036377 11.55689941 -14.45494629 10.38723633 -14.4699707 9.18212891 C-13.90520049 5.35812113 -12.67713315 3.73288729 -10 1 C-6.81092735 -0.59453633 -3.47055318 -0.38561702 0 0 Z " fill="#0B0B0B" transform="translate(191,16)"/>
<path d="M0 0 C11.55 0 23.1 0 35 0 C31.16714536 4.47166374 27.41148603 8.72208437 23.234375 12.828125 C22.71490295 13.34381042 22.19543091 13.85949585 21.66021729 14.39080811 C20.02592963 16.01303596 18.38839822 17.6319245 16.75 19.25 C14.57522801 21.39814213 12.40382326 23.54960801 10.234375 25.703125 C9.73854919 26.19065247 9.24272339 26.67817993 8.73187256 27.18048096 C6.52839537 29.36816882 4.73153046 31.4027043 3 34 C2.34 34 1.68 34 1 34 C0.67 35.32 0.34 36.64 0 38 C0 25.46 0 12.92 0 0 Z " fill="#FEFEFE" transform="translate(0,0)"/>
<path d="M0 0 C1.98 0 3.96 0 6 0 C7.53418693 7.17463797 8.67336665 14.35180227 9.625 21.625 C9.7590625 22.62917969 9.893125 23.63335937 10.03125 24.66796875 C10.35706877 27.11160952 10.67993814 29.55559973 11 32 C11.17390259 30.65252686 11.17390259 30.65252686 11.35131836 29.27783203 C11.78267485 25.94899297 12.21725961 22.62060877 12.65405273 19.29248047 C12.84252716 17.85124362 13.02970858 16.40983707 13.21557617 14.96826172 C13.48277447 12.89765308 13.75459839 10.82769683 14.02734375 8.7578125 C14.18968506 7.5114502 14.35202637 6.26508789 14.51928711 4.98095703 C15 2 15 2 16 0 C17.65 0 19.3 0 21 0 C20.45298808 9.72684762 19.10694898 19.22681907 17.4375 28.8125 C17.20063243 30.19847562 16.96430153 31.58454308 16.72851562 32.97070312 C16.1575828 36.31473823 15.58132887 39.65775933 15 43 C12.03 43 9.06 43 6 43 C3.03 21.715 3.03 21.715 0 0 Z " fill="#020202" transform="translate(140,16)"/>
<path d="M0 0 C5.94 0 11.88 0 18 0 C18 1.98 18 3.96 18 6 C14.04 6 10.08 6 6 6 C6 9.63 6 13.26 6 17 C8.97 17 11.94 17 15 17 C15 18.98 15 20.96 15 23 C12.03 23 9.06 23 6 23 C6 27.62 6 32.24 6 37 C9.96 37 13.92 37 18 37 C18 38.98 18 40.96 18 43 C12.06 43 6.12 43 0 43 C0 28.81 0 14.62 0 0 Z " fill="#000000" transform="translate(119,16)"/>
<path d="M0 0 C6.93 0 13.86 0 21 0 C21 1.98 21 3.96 21 6 C18.36 6 15.72 6 13 6 C13 18.21 13 30.42 13 43 C11.02 43 9.04 43 7 43 C7 30.79 7 18.58 7 6 C4.69 6 2.38 6 0 6 C0 4.02 0 2.04 0 0 Z " fill="#010101" transform="translate(201,16)"/>
<path d="M0 0 C1.98 0 3.96 0 6 0 C6 14.19 6 28.38 6 43 C4.02 43 2.04 43 0 43 C0 28.81 0 14.62 0 0 Z " fill="#000000" transform="translate(166,16)"/>
<path d="M0 0 C1.98 0 3.96 0 6 0 C8.72234602 2.72234602 8.25788057 4.22762873 8.265625 8.03125 C8.26820312 9.23394531 8.27078125 10.43664062 8.2734375 11.67578125 C8.26570313 12.93777344 8.25796875 14.19976562 8.25 15.5 C8.25773437 16.76199219 8.26546875 18.02398437 8.2734375 19.32421875 C8.27085938 20.52691406 8.26828125 21.72960937 8.265625 22.96875 C8.26336914 24.07669922 8.26111328 25.18464844 8.25878906 26.32617188 C8 29 8 29 6 31 C4.02 31 2.04 31 0 31 C0 20.77 0 10.54 0 0 Z " fill="#EAEAEA" transform="translate(100,22)"/>
<path d="M0 0 C3.53571429 0.53571429 3.53571429 0.53571429 5 2 C5.10003289 4.13353174 5.13081048 6.27037542 5.1328125 8.40625 C5.13474609 10.35724609 5.13474609 10.35724609 5.13671875 12.34765625 C5.13462402 13.02844238 5.1325293 13.70922852 5.13037109 14.41064453 C5.1250224 16.49128515 5.1303168 18.57170989 5.13671875 20.65234375 C5.13542969 21.95300781 5.13414062 23.25367188 5.1328125 24.59375 C5.13168457 25.79708984 5.13055664 27.00042969 5.12939453 28.24023438 C5 31 5 31 4 32 C1.5 32.125 1.5 32.125 -1 32 C-3.33716009 29.66283991 -2.17162619 23.81167112 -2.18554688 20.70117188 C-2.18546631 20.0223999 -2.18538574 19.34362793 -2.18530273 18.64428711 C-2.18748734 16.57449736 -2.20563491 14.50524207 -2.22460938 12.43554688 C-2.22654297 11.13810547 -2.22847656 9.84066406 -2.23046875 8.50390625 C-2.23457764 7.30580322 -2.23868652 6.1077002 -2.24291992 4.87329102 C-2 2 -2 2 0 0 Z " fill="#FAFAFA" transform="translate(186,21)"/>
</svg>
                    </div>
                    <p style={{ color: '#888', fontSize: '14px', margin: '4px 0 0' }}>Logga in på ditt konto</p>
                </div>

                {/* Error / info */}
                {error && (
                    <div style={{
                        backgroundColor: '#f8d7da', color: '#721c24', padding: '10px 14px',
                        borderRadius: '6px', marginBottom: '20px', fontSize: '14px', textAlign: 'center'
                    }}>
                        {error}
                    </div>
                )}
                {info && !error && (
                    <div style={{
                        backgroundColor: '#d1ecf1', color: '#0c5460', padding: '10px 14px',
                        borderRadius: '6px', marginBottom: '20px', fontSize: '14px', textAlign: 'center'
                    }}>
                        {info}
                    </div>
                )}

                {emailStep === 'enterEmail' && (
                    <form onSubmit={handleRequestCode}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>E-post</label>
                            <input
                                type="email"
                                required
                                autoFocus
                                autoComplete="email"
                                value={email}
                                onChange={e => { setEmail(e.target.value); setEmailIsSaved(false); }}
                                style={inputStyle}
                                onFocus={e => e.target.style.borderColor = '#007bff'}
                                onBlur={e => e.target.style.borderColor = '#ddd'}
                            />
                            {emailIsSaved && (
                                <div style={{ marginTop: '6px', fontSize: '13px', color: '#666' }}>
                                    Inte du?{' '}
                                    <button
                                        type="button"
                                        style={linkButtonStyle}
                                        onClick={() => { clearSavedLoginEmail(); setEmail(''); setEmailIsSaved(false); }}
                                    >
                                        Byt adress
                                    </button>
                                </div>
                            )}
                        </div>
                        <button type="submit" disabled={loading} style={buttonStyle(loading)}>
                            {loading ? 'Skickar…' : 'Skicka mig en kod'}
                        </button>
                    </form>
                )}

                {emailStep === 'enterCode' && (
                    <form onSubmit={handleVerifyCode}>
                        <div style={{ marginBottom: '12px', fontSize: '13px', color: '#666' }}>
                            Koden är skickad till <strong>{email}</strong>
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Sexsiffrig kod</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={6}
                                required
                                autoFocus
                                autoComplete="one-time-code"
                                value={code}
                                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                                style={{ ...inputStyle, letterSpacing: '8px', textAlign: 'center', fontSize: '20px', fontWeight: 'bold' }}
                                onFocus={e => e.target.style.borderColor = '#007bff'}
                                onBlur={e => e.target.style.borderColor = '#ddd'}
                            />
                        </div>
                        <button type="submit" disabled={loading || code.length !== 6} style={buttonStyle(loading || code.length !== 6)}>
                            {loading ? 'Kontrollerar…' : 'Logga in'}
                        </button>
                        <div style={{ textAlign: 'center', marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button
                                type="button"
                                style={linkButtonStyle}
                                disabled={loading}
                                onClick={handleRequestCode}
                            >
                                Skicka en ny kod
                            </button>
                            <button
                                type="button"
                                style={linkButtonStyle}
                                onClick={() => { resetMessages(); clearPendingCode(); setEmailStep('enterEmail'); setCode(''); }}
                            >
                                Använd en annan e-postadress
                            </button>
                        </div>
                    </form>
                )}

                {lastLogout && (
                    <div style={{ marginTop: '24px', fontSize: '11px', color: '#999', textAlign: 'center', wordBreak: 'break-all' }}>
                        Utloggad {new Date(lastLogout.at).toLocaleString('sv-SE')}: {lastLogout.request}
                        {lastLogout.message ? ` (${lastLogout.message})` : ''}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Login;
